/**
 * https://github.com/WICG/focus-visible
 */
function init() {
  var hadKeyboardEvent = true;
  var hadFocusVisibleRecently = false;
  var hadFocusVisibleRecentlyTimeout = null;

  var inputTypesWhitelist = {
    text: true,
    search: true,
    url: true,
    tel: true,
    email: true,
    password: true,
    number: true,
    date: true,
    month: true,
    week: true,
    time: true,
    datetime: true,
    'datetime-local': true
  };

  /**
   * Helper function for legacy browsers and iframes which sometimes focus
   * elements like document, body, and non-interactive SVG.
   * @param {Element} el
   */
  function isValidFocusTarget(el) {
    if (
      el &&
      el !== document &&
      el.nodeName !== 'HTML' &&
      el.nodeName !== 'BODY' &&
      'classList' in el &&
      'contains' in el.classList
    ) {
      return true;
    }
    return false;
  }

  /**
   * Computes whether the given element should automatically trigger the
   * `focus-visible` class being added, i.e. whether it should always match
   * `:focus-visible` when focused.
   * @param {Element} el
   * @return {boolean}
   */
  function focusTriggersKeyboardModality(el) {
    var type = el.type;
    var tagName = el.tagName;

    if (tagName == 'INPUT' && inputTypesWhitelist[type] && !el.readOnly) {
      return true;
    }

    if (tagName == 'TEXTAREA' && !el.readOnly) {
      return true;
    }

    if (el.isContentEditable) {
      return true;
    }

    return false;
  }

  /**
   * Add the `focus-visible` class to the given element if it was not added by
   * the author.
   * @param {Element} el
   */
  function addFocusVisibleClass(el) {
    if (el.classList.contains('focus-visible')) {
      return;
    }
    el.classList.add('focus-visible');
    el.setAttribute('data-focus-visible-added', '');
  }

  /**
   * Remove the `focus-visible` class from the given element if it was not
   * originally added by the author.
   * @param {Element} el
   */
  function removeFocusVisibleClass(el) {
    if (!el.hasAttribute('data-focus-visible-added')) {
      return;
    }
    el.classList.remove('focus-visible');
    el.removeAttribute('data-focus-visible-added');
  }

  /**
   * Treat `keydown` as a signal that the user is in keyboard modality.
   * Apply `focus-visible` to any current active element and keep track
   * of our keyboard modality state with `hadKeyboardEvent`.
   * @param {Event} e
   */
  function onKeyDown(e) {
    if (isValidFocusTarget(document.activeElement)) {
      addFocusVisibleClass(document.activeElement);
    }

    hadKeyboardEvent = true;
  }

  /**
   * If at any point a user clicks with a pointing device, ensure that we change
   * the modality away from keyboard.
   * This avoids the situation where a user presses a key on an already focused
   * element, and then clicks on a different element, focusing it with a
   * pointing device, while we still think we're in keyboard modality.
   * @param {Event} e
   */
  function onPointerDown(e) {
    hadKeyboardEvent = false;
  }

  /**
   * On `focus`, add the `focus-visible` class to the target if:
   * - the target received focus as a result of keyboard navigation, or
   * - the event target is an element that will likely require interaction
   *   via the keyboard (e.g. a text box)
   * @param {Event} e
   */
  function onFocus(e) {
    // Prevent IE from focusing the document or HTML element.
    if (!isValidFocusTarget(e.target)) {
      return;
    }

    if (hadKeyboardEvent || focusTriggersKeyboardModality(e.target)) {
      addFocusVisibleClass(e.target);
    }
  }

  /**
   * On `blur`, remove the `focus-visible` class from the target.
   * @param {Event} e
   */
  function onBlur(e) {
    if (!isValidFocusTarget(e.target)) {
      return;
    }

    if (
      e.target.classList.contains('focus-visible') ||
      e.target.hasAttribute('data-focus-visible-added')
    ) {
      // To detect a tab/window switch, we look for a blur event followed
      // rapidly by a visibility change.
      // If we don't see a visibility change within 100ms, it's probably a
      // regular focus change.
      hadFocusVisibleRecently = true;
      window.clearTimeout(hadFocusVisibleRecentlyTimeout);
      hadFocusVisibleRecentlyTimeout = window.setTimeout(function () {
        hadFocusVisibleRecently = false;
        window.clearTimeout(hadFocusVisibleRecentlyTimeout);
      }, 100);
      removeFocusVisibleClass(e.target);
    }
  }

  /**
   * If the user changes tabs, keep track of whether or not the previously
   * focused element had .focus-visible.
   * @param {Event} e
   */
  function onVisibilityChange(e) {
    if (document.visibilityState == 'hidden') {
      // If the tab becomes active again, the browser will handle calling focus
      // on the element (Safari actually calls it twice).
      // If this tab change caused a blur on an element with focus-visible,
      // re-apply the class when the user switches back to the tab.
      if (hadFocusVisibleRecently) {
        hadKeyboardEvent = true;
      }
      addInitialPointerMoveListeners();
    }
  }

  /**
   * Add a group of listeners to detect usage of any pointing devices.
   * These listeners will be added when the polyfill first loads, and anytime
   * the window is blurred, so that they are active when the window regains
   * focus.
   */
  function addInitialPointerMoveListeners() {
    document.addEventListener('mousemove', onInitialPointerMove);
    document.addEventListener('mousedown', onInitialPointerMove);
    document.addEventListener('mouseup', onInitialPointerMove);
    document.addEventListener('pointermove', onInitialPointerMove);
    document.addEventListener('pointerdown', onInitialPointerMove);
    document.addEventListener('pointerup', onInitialPointerMove);
    document.addEventListener('touchmove', onInitialPointerMove);
    document.addEventListener('touchstart', onInitialPointerMove);
    document.addEventListener('touchend', onInitialPointerMove);
  }

  function removeInitialPointerMoveListeners() {
    document.removeEventListener('mousemove', onInitialPointerMove);
    document.removeEventListener('mousedown', onInitialPointerMove);
    document.removeEventListener('mouseup', onInitialPointerMove);
    document.removeEventListener('pointermove', onInitialPointerMove);
    document.removeEventListener('pointerdown', onInitialPointerMove);
    document.removeEventListener('pointerup', onInitialPointerMove);
    document.removeEventListener('touchmove', onInitialPointerMove);
    document.removeEventListener('touchstart', onInitialPointerMove);
    document.removeEventListener('touchend', onInitialPointerMove);
  }

  /**
   * When the polfyill first loads, assume the user is in keyboard modality.
   * If any event is received from a pointing device (e.g. mouse, pointer,
   * touch), turn off keyboard modality.
   * This accounts for situations where focus enters the page from the URL bar.
   * @param {Event} e
   */
  function onInitialPointerMove(e) {
    // Work around a Safari quirk that fires a mousemove on <html> whenever the
    // window blurs, even if you're tabbing out of the page. ¯\_(ツ)_/¯
    if (e.target.nodeName.toLowerCase() === 'html') {
      return;
    }

    hadKeyboardEvent = false;
    removeInitialPointerMoveListeners();
  }

  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('mousedown', onPointerDown, true);
  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('touchstart', onPointerDown, true);
  document.addEventListener('focus', onFocus, true);
  document.addEventListener('blur', onBlur, true);
  document.addEventListener('visibilitychange', onVisibilityChange, true);
  addInitialPointerMoveListeners();

  document.body.classList.add('js-focus-visible');
}

/**
 * Subscription when the DOM is ready
 * @param {Function} callback
 */
function onDOMReady(callback) {
  var loaded;

  /**
   * Callback wrapper for check loaded state
   */
  function load() {
    if (!loaded) {
      loaded = true;

      callback();
    }
  }

  if (['interactive', 'complete'].indexOf(document.readyState) >= 0) {
    callback();
  } else {
    loaded = false;
    document.addEventListener('DOMContentLoaded', load, false);
    window.addEventListener('load', load, false);
  }
}

if (typeof document !== 'undefined') {
  onDOMReady(init);
}
/* ===========================================================
 * onepagescroll.js v1.2.2
 * ===========================================================
 * Copyright 2014 Pete Rojwongsuriya.
 * http://www.thepetedesign.com
 *
 * Create an Apple-like website that let user scroll
 * one page at a time
 *
 * Credit: Eike Send for the awesome swipe event
 * https://github.com/peachananr/purejs-onepage-scroll
 * 
 * License: GPL v3
 *
 * ========================================================== */

function onePageScroll(element, options) {

  var defaults = {
    sectionContainer: "section",
    easing: "ease",
    animationTime: 1000,
    pagination: true,
    updateURL: false,
    keyboard: true,
    beforeMove: null,
    afterMove: null,
    loop: false,
    responsiveFallback: false
  },
    _root = this,
    settings = Object.extend({}, defaults, options),
    el = document.querySelector(element),
    sections = document.querySelectorAll(settings.sectionContainer),
    total = sections.length,
    status = "off",
    topPos = 0,
    lastAnimation = 0,
    quietPeriod = 500,
    paginationList = "",
    body = document.querySelector("body");

  this.init = function () {
    /*-------------------------------------------*/
    /*  Prepare Everything                       */
    /*-------------------------------------------*/

    _addClass(el, "onepage-wrapper")
    el.style.position = "relative";

    for (var i = 0; i < sections.length; i++) {
      _addClass(sections[i], "ops-section")
      sections[i].dataset.index = i + 1;
      topPos = topPos + 100;

      if (settings.pagination == true) {
        paginationList += "<li><a data-index='" + (i + 1) + "' href='#" + (i + 1) + "'></a></li>";
      }
    }

    _swipeDownHandler = function (event) {

      if (!document.documentElement.classList.contains(JohnLocke.lockClass)) {

        if (!_hasClass(body, "disabled-onepage-scroll")) event.preventDefault();
        moveUp(el);
      }

    }

    _swipeUpHandler = function (event) {

      if (!document.documentElement.classList.contains(JohnLocke.lockClass)) {

        if (!_hasClass(body, "disabled-onepage-scroll")) event.preventDefault();
        moveDown(el);

      }

    }

    _swipeEvents(el);
    document.addEventListener("swipeDown", _swipeDownHandler);
    document.addEventListener("swipeUp", _swipeUpHandler);

    // Create Pagination and Display Them

    if (settings.pagination == true) {
      var pagination = document.createElement("ul");
      pagination.setAttribute("class", "onepage-pagination");

      body.appendChild(pagination)
      pagination.innerHTML = paginationList;
      var posTop = (document.querySelector(".onepage-pagination").offsetHeight / 2) * -1;
      document.querySelector(".onepage-pagination").style.marginTop = posTop;
    }

    if (window.location.hash != "" && window.location.hash != "#1") {
      var init_index = window.location.hash.replace("#", ""),
        next = document.querySelector(settings.sectionContainer + "[data-index='" + (init_index) + "']"),
        next_index = next.dataset.index;

      _addClass(document.querySelector(settings.sectionContainer + "[data-index='" + init_index + "']"), "active")
      _addClass(body, "viewing-page-" + init_index)
      if (settings.pagination == true) _addClass(document.querySelector(".onepage-pagination li a" + "[data-index='" + init_index + "']"), "active");

      if (next) {
        _addClass(next, "active")
        if (settings.pagination == true) _addClass(document.querySelector(".onepage-pagination li a" + "[data-index='" + init_index + "']"), "active");

        body.className = body.className.replace(/\bviewing-page-\d.*?\b/g, '');
        _addClass(body, "viewing-page-" + next_index)
        if (history.replaceState && settings.updateURL == true) {
          var href = window.location.href.substr(0, window.location.href.indexOf('#')) + "#" + (init_index);
          history.pushState({}, document.title, href);
        }
      }
      var pos = ((init_index - 1) * 100) * -1;
      _transformPage(el, settings, pos, init_index);

    } else {
      _addClass(document.querySelector(settings.sectionContainer + "[data-index='1']"), "active");
      _addClass(body, "viewing-page-1");
      if (settings.pagination == true) _addClass(document.querySelector(".onepage-pagination li a[data-index='1']"), "active");
    }

    _paginationHandler = function () {
      var page_index = this.dataset.index;
      moveTo(el, page_index);
    }


    if (settings.pagination == true) {
      var pagination_links = document.querySelectorAll(".onepage-pagination li a");

      for (var i = 0; i < pagination_links.length; i++) {
        pagination_links[i].addEventListener('click', _paginationHandler);
      }
    }

    _mouseWheelHandler = function (event) {
      event.preventDefault();
      var delta = event.wheelDelta || -event.detail;
      if (!_hasClass(body, "disabled-onepage-scroll")) _init_scroll(event, delta);
    }

    document.addEventListener('mousewheel', _mouseWheelHandler);
    document.addEventListener('DOMMouseScroll', _mouseWheelHandler);


    if (settings.responsiveFallback != false) {
      window.onresize = function () {
        _responsive();
      }

      _responsive();
    }

    _keydownHandler = function (e) {
      var tag = e.target.tagName.toLowerCase();

      if (!_hasClass(body, "disabled-onepage-scroll")) {
        switch (e.which) {
          case 38:
            if (tag != 'input' && tag != 'textarea') moveUp(el)
            break;
          case 40:
            if (tag != 'input' && tag != 'textarea') moveDown(el)
            break;
          default: return;
        }
      }
      return false;
    }

    if (settings.keyboard == true) {
      document.onkeydown = _keydownHandler;
    }
    return false;
  }

  /*-------------------------------------------------------*/
  /*  Private Functions                                    */
  /*-------------------------------------------------------*/
  /*------------------------------------------------*/
  /*  Credit: Eike Send for the awesome swipe event */
  /*------------------------------------------------*/
  _swipeEvents = function (el) {
    var startX,
      startY;

    document.addEventListener("touchstart", touchstart);

    function touchstart(event) {
      var touches = event.touches;
      if (touches && touches.length) {
        startX = touches[0].pageX;
        startY = touches[0].pageY;
        document.addEventListener("touchmove", touchmove);
      }
    }

    function touchmove(event) {


      var touches = event.touches;
      if (touches && touches.length) {
        event.preventDefault();
        var deltaX = startX - touches[0].pageX;
        var deltaY = startY - touches[0].pageY;

        if (deltaX >= 50) {
          var event = new Event('swipeLeft');
          document.dispatchEvent(event);
        }
        if (deltaX <= -50) {
          var event = new Event('swipeRight');
          document.dispatchEvent(event);
        }
        if (deltaY >= 50) {
          var event = new Event('swipeUp');
          document.dispatchEvent(event);
        }
        if (deltaY <= -50) {
          var event = new Event('swipeDown');
          document.dispatchEvent(event);
        }

        if (Math.abs(deltaX) >= 50 || Math.abs(deltaY) >= 50) {
          document.removeEventListener('touchmove', touchmove);
        }
      }


    }

  };
  /*-----------------------------------------------------------*/
  /*  Utility to add/remove class easily with javascript       */
  /*-----------------------------------------------------------*/

  _trim = function (str) {
    return str.replace(/^\s\s*/, '').replace(/\s\s*$/, '');
  }

  _hasClass = function (ele, cls) {
    if (ele.className) {
      return ele.className.match(new RegExp('(\\s|^)' + cls + '(\\s|$)'));
    } else {
      return ele.className = cls;
    }
  }

  _addClass = function (ele, cls) {
    if (!_hasClass(ele, cls)) ele.className += " " + cls;
    ele.className = _trim(ele.className)
  }

  _removeClass = function (ele, cls) {
    if (_hasClass(ele, cls)) {
      var reg = new RegExp('(\\s|^)' + cls + '(\\s|$)');
      ele.className = ele.className.replace(reg, ' ');
    }
    ele.className = _trim(ele.className)
  }

  /*-----------------------------------------------------------*/
  /*  Transtionend Normalizer by Modernizr                     */
  /*-----------------------------------------------------------*/

  _whichTransitionEvent = function () {
    var t;
    var el = document.createElement('fakeelement');
    var transitions = {
      'transition': 'transitionend',
      'OTransition': 'oTransitionEnd',
      'MozTransition': 'transitionend',
      'WebkitTransition': 'webkitTransitionEnd'
    }

    for (t in transitions) {
      if (el.style[t] !== undefined) {
        return transitions[t];
      }
    }
  }

  /*-----------------------------------------------------------*/
  /*  Function to perform scroll to top animation              */
  /*-----------------------------------------------------------*/

  _scrollTo = function (element, to, duration) {
    if (duration < 0) return;
    var difference = to - element.scrollTop;
    var perTick = difference / duration * 10;

    setTimeout(function () {
      element.scrollTop = element.scrollTop + perTick;
      if (element.scrollTop == to) return;
      _scrollTo(element, to, duration - 10);
    }, 10);
  }



  /*---------------------------------*/
  /*  Function to transform the page */
  /*---------------------------------*/

  _transformPage = function (el2, settings, pos, index, next_el) {
    if (typeof settings.beforeMove == 'function') settings.beforeMove(index, next_el);

    var transformCSS = "-webkit-transform: translate3d(0, " + pos + "%, 0); -webkit-transition: -webkit-transform " + settings.animationTime + "ms " + settings.easing + "; -moz-transform: translate3d(0, " + pos + "%, 0); -moz-transition: -moz-transform " + settings.animationTime + "ms " + settings.easing + "; -ms-transform: translate3d(0, " + pos + "%, 0); -ms-transition: -ms-transform " + settings.animationTime + "ms " + settings.easing + "; transform: translate3d(0, " + pos + "%, 0); transition: transform " + settings.animationTime + "ms " + settings.easing + ";";

    el2.style.cssText = transformCSS;

    var transitionEnd = _whichTransitionEvent();
    el2.addEventListener(transitionEnd, endAnimation, false);

    function endAnimation() {
      if (typeof settings.afterMove == 'function') settings.afterMove(index, next_el);
      el2.removeEventListener(transitionEnd, endAnimation)
    }
  }

  /*-------------------------------------------*/
  /*  Responsive Fallback trigger              */
  /*-------------------------------------------*/

  _responsive = function () {

    if (document.body.clientWidth < settings.responsiveFallback) {

      _addClass(body, "disabled-onepage-scroll");
      document.documentElement.classList.add('html--one-page-scroll-disabled');
      document.removeEventListener('mousewheel', _mouseWheelHandler);
      document.removeEventListener('DOMMouseScroll', _mouseWheelHandler);
      _swipeEvents(el);
      document.removeEventListener("swipeDown", _swipeDownHandler);
      document.removeEventListener("swipeUp", _swipeUpHandler);

    } else {

      if (_hasClass(body, "disabled-onepage-scroll")) {
        _removeClass(body, "disabled-onepage-scroll");
        document.documentElement.classList.remove('html--one-page-scroll-disabled');
        _scrollTo(document.documentElement, 0, 2000);
      }



      _swipeEvents(el);
      document.addEventListener("swipeDown", _swipeDownHandler);
      document.addEventListener("swipeUp", _swipeUpHandler);

      document.addEventListener('mousewheel', _mouseWheelHandler);
      document.addEventListener('DOMMouseScroll', _mouseWheelHandler);

    }
  }

  /*-------------------------------------------*/
  /*  Initialize scroll detection              */
  /*-------------------------------------------*/

  _init_scroll = function (event, delta) {
    var deltaOfInterest = delta,
      timeNow = new Date().getTime();

    // Cancel scroll if currently animating or within quiet period
    if (timeNow - lastAnimation < quietPeriod + settings.animationTime) {
      event.preventDefault();
      return;
    }

    if (deltaOfInterest < 0) {
      moveDown(el)
    } else {
      moveUp(el)
    }

    lastAnimation = timeNow;
  }


  /*-------------------------------------------------------*/
  /*  Public Functions                                     */
  /*-------------------------------------------------------*/

  /*---------------------------------*/
  /*  Function to move down section  */
  /*---------------------------------*/

  this.moveDown = function (el3) {

    if (typeof el3 == "string") el3 = document.querySelector(el3);

    var index = document.querySelector(settings.sectionContainer + ".active").dataset.index,
      current = document.querySelector(settings.sectionContainer + "[data-index='" + index + "']"),
      next = document.querySelector(settings.sectionContainer + "[data-index='" + (parseInt(index) + 1) + "']");


    if (!next) {
      if (settings.loop == true) {
        pos = 0;
        next = document.querySelector(settings.sectionContainer + "[data-index='1']");
      } else {
        return
      }

    } else {
      pos = (index * 100) * -1;
    }
    var next_index = next.dataset.index;
    _removeClass(current, "active");
    _addClass(next, "active");

    if (settings.pagination == true) {
      _removeClass(document.querySelector(".onepage-pagination li a" + "[data-index='" + index + "']"), "active");
      _addClass(document.querySelector(".onepage-pagination li a" + "[data-index='" + next_index + "']"), "active");
    }

    body.className = body.className.replace(/\bviewing-page-\d.*?\b/g, '');
    _addClass(body, "viewing-page-" + next_index);

    if (history.replaceState && settings.updateURL == true) {
      var href = window.location.href.substr(0, window.location.href.indexOf('#')) + "#" + (parseInt(index) + 1);
      history.pushState({}, document.title, href);
    }
    _transformPage(el3, settings, pos, next_index, next);
  }

  /*---------------------------------*/
  /*  Function to move up section    */
  /*---------------------------------*/

  this.moveUp = function (el4) {

    if (typeof el4 == "string") el4 = document.querySelector(el4);

    var index = document.querySelector(settings.sectionContainer + ".active").dataset.index,
      current = document.querySelector(settings.sectionContainer + "[data-index='" + index + "']"),
      next = document.querySelector(settings.sectionContainer + "[data-index='" + (parseInt(index) - 1) + "']");

    if (!next) {
      if (settings.loop == true) {
        pos = ((total - 1) * 100) * -1;
        next = document.querySelector(settings.sectionContainer + "[data-index='" + total + "']");
      } else {
        return
      }
    } else {
      pos = ((next.dataset.index - 1) * 100) * -1;
    }
    var next_index = next.dataset.index;
    _removeClass(current, "active")
    _addClass(next, "active")

    if (settings.pagination == true) {
      _removeClass(document.querySelector(".onepage-pagination li a" + "[data-index='" + index + "']"), "active");
      _addClass(document.querySelector(".onepage-pagination li a" + "[data-index='" + next_index + "']"), "active");
    }
    body.className = body.className.replace(/\bviewing-page-\d.*?\b/g, '');
    _addClass(body, "viewing-page-" + next_index);

    if (history.replaceState && settings.updateURL == true) {
      var href = window.location.href.substr(0, window.location.href.indexOf('#')) + "#" + (parseInt(index) - 1);
      history.pushState({}, document.title, href);
    }
    _transformPage(el4, settings, pos, next_index, next);
  }

  /*-------------------------------------------*/
  /*  Function to move to specified section    */
  /*-------------------------------------------*/

  this.moveTo = function (el5, page_index) {

    if (typeof el5 == "string") el5 = document.querySelector(el5);

    var current = document.querySelector(settings.sectionContainer + ".active"),
      next = document.querySelector(settings.sectionContainer + "[data-index='" + (page_index) + "']");

    if (next) {
      var next_index = next.dataset.index;
      _removeClass(current, "active");
      _addClass(next, "active");
      _removeClass(document.querySelector(".onepage-pagination li a" + ".active"), "active");
      _addClass(document.querySelector(".onepage-pagination li a" + "[data-index='" + (page_index) + "']"), "active");

      body.className = body.className.replace(/\bviewing-page-\d.*?\b/g, '');
      _addClass(body, "viewing-page-" + next_index);

      pos = ((page_index - 1) * 100) * -1;

      if (history.replaceState && settings.updateURL == true) {
        var href = window.location.href.substr(0, window.location.href.indexOf('#')) + "#" + (parseInt(page_index) - 1);
        history.pushState({}, document.title, href);
      }
      _transformPage(el5, settings, pos, page_index, next);
    }
  }

  /*-------------------------*/
  /*  Function to disable    */
  /*-------------------------*/

  this.disableOnePageScroll = function () {

    document.removeEventListener('mousewheel', _mouseWheelHandler);
    document.removeEventListener('DOMMouseScroll', _mouseWheelHandler);
    
  }

  /*------------------------*/
  /*  Function to enable    */
  /*------------------------*/

  this.enableOnePageScroll = function () {

    document.addEventListener('mousewheel', _mouseWheelHandler);
    document.addEventListener('DOMMouseScroll', _mouseWheelHandler);

  }

  this.init();
}

/*------------------------------------------------*/
/*  Ulitilities Method                            */
/*------------------------------------------------*/

/*-----------------------------------------------------------*/
/*  Function by John Resig to replicate extend functionality */
/*-----------------------------------------------------------*/

Object.extend = function (orig) {
  if (orig == null)
    return orig;

  for (var i = 1; i < arguments.length; i++) {
    var obj = arguments[i];
    if (obj != null) {
      for (var prop in obj) {
        var getter = obj.__lookupGetter__(prop),
          setter = obj.__lookupSetter__(prop);

        if (getter || setter) {
          if (getter)
            orig.__defineGetter__(prop, getter);
          if (setter)
            orig.__defineSetter__(prop, setter);
        } else {
          orig[prop] = obj[prop];
        }
      }
    }
  }

  return orig;
};


/*!
 * Splide.js
 * Version  : 2.4.20
 * License  : MIT
 * Copyright: 2020 Naotoshi Fujita
 */!function () { "use strict"; var t = { d: function (n, e) { for (var i in e) t.o(e, i) && !t.o(n, i) && Object.defineProperty(n, i, { enumerable: !0, get: e[i] }) }, o: function (t, n) { return Object.prototype.hasOwnProperty.call(t, n) }, r: function (t) { "undefined" != typeof Symbol && Symbol.toStringTag && Object.defineProperty(t, Symbol.toStringTag, { value: "Module" }), Object.defineProperty(t, "__esModule", { value: !0 }) } }, n = {}; t.r(n), t.d(n, { CREATED: function () { return R }, DESTROYED: function () { return X }, IDLE: function () { return F }, MOUNTED: function () { return B }, MOVING: function () { return G } }); function e() { return (e = Object.assign || function (t) { for (var n = 1; n < arguments.length; n++) { var e = arguments[n]; for (var i in e) Object.prototype.hasOwnProperty.call(e, i) && (t[i] = e[i]) } return t }).apply(this, arguments) } var i = Object.keys; function o(t, n) { i(t).some((function (e, i) { return n(t[e], e, i) })) } function r(t) { return i(t).map((function (n) { return t[n] })) } function s(t) { return "object" == typeof t } function a(t, n) { var i = e({}, t); return o(n, (function (t, n) { s(t) ? (s(i[n]) || (i[n] = {}), i[n] = a(i[n], t)) : i[n] = t })), i } function u(t) { return Array.isArray(t) ? t : [t] } function c(t, n, e) { return Math.min(Math.max(t, n > e ? e : n), n > e ? n : e) } function d(t, n) { var e = 0; return t.replace(/%s/g, (function () { return u(n)[e++] })) } function f(t) { var n = typeof t; return "number" === n && t > 0 ? parseFloat(t) + "px" : "string" === n ? t : "" } function l(t) { return t < 10 ? "0" + t : t } function h(t, n) { if ("string" == typeof n) { var e = m("div", {}); E(e, { position: "absolute", width: n }), w(t, e), n = e.clientWidth, b(e) } return +n || 0 } function p(t, n) { return t ? t.querySelector(n.split(" ")[0]) : null } function g(t, n) { return v(t, n)[0] } function v(t, n) { return t ? r(t.children).filter((function (t) { return P(t, n.split(" ")[0]) || t.tagName === n })) : [] } function m(t, n) { var e = document.createElement(t); return o(n, (function (t, n) { return C(e, n, t) })), e } function y(t) { var n = m("div", {}); return n.innerHTML = t, n.firstChild } function b(t) { u(t).forEach((function (t) { if (t) { var n = t.parentElement; n && n.removeChild(t) } })) } function w(t, n) { t && t.appendChild(n) } function x(t, n) { if (t && n) { var e = n.parentElement; e && e.insertBefore(t, n) } } function E(t, n) { t && o(n, (function (n, e) { null !== n && (t.style[e] = n) })) } function _(t, n, e) { t && u(n).forEach((function (n) { n && t.classList[e ? "remove" : "add"](n) })) } function k(t, n) { _(t, n, !1) } function S(t, n) { _(t, n, !0) } function P(t, n) { return !!t && t.classList.contains(n) } function C(t, n, e) { t && t.setAttribute(n, e) } function z(t, n) { return t ? t.getAttribute(n) : "" } function I(t, n) { u(n).forEach((function (n) { u(t).forEach((function (t) { return t && t.removeAttribute(n) })) })) } function M(t) { return t.getBoundingClientRect() } var T = "slide", A = "loop", O = "fade", L = function (t, n) { var e, i; return { mount: function () { e = n.Elements.list, t.on("transitionend", (function (t) { t.target === e && i && i() }), e) }, start: function (o, r, s, a, u) { var c = t.options, d = n.Controller.edgeIndex, f = c.speed; i = u, t.is(T) && (0 === s && r >= d || s >= d && 0 === r) && (f = c.rewindSpeed || f), E(e, { transition: "transform " + f + "ms " + c.easing, transform: "translate(" + a.x + "px," + a.y + "px)" }) } } }, W = function (t, n) { function e(e) { var i = t.options; E(n.Elements.slides[e], { transition: "opacity " + i.speed + "ms " + i.easing }) } return { mount: function () { e(t.index) }, start: function (t, i, o, r, s) { var a = n.Elements.track; E(a, { height: f(a.clientHeight) }), e(i), setTimeout((function () { s(), E(a, { height: "" }) })) } } }; function H(t) { console.error("[SPLIDE] " + t) } function j(t, n) { if (!t) throw new Error(n) } var q = "splide", D = { active: "is-active", visible: "is-visible", loading: "is-loading" }, N = { type: "slide", rewind: !1, speed: 400, rewindSpeed: 0, waitForTransition: !0, width: 0, height: 0, fixedWidth: 0, fixedHeight: 0, heightRatio: 0, autoWidth: !1, autoHeight: !1, perPage: 1, perMove: 0, clones: 0, start: 0, focus: !1, gap: 0, padding: 0, arrows: !0, arrowPath: "", pagination: !0, autoplay: !1, interval: 5e3, pauseOnHover: !0, pauseOnFocus: !0, resetProgress: !0, lazyLoad: !1, preloadPages: 1, easing: "cubic-bezier(.42,.65,.27,.99)", keyboard: "global", drag: !0, dragAngleThreshold: 30, swipeDistanceThreshold: 150, flickVelocityThreshold: .6, flickPower: 600, flickMaxPages: 1, direction: "ltr", cover: !1, accessibility: !0, slideFocus: !0, isNavigation: !1, trimSpace: !0, updateOnMove: !1, throttle: 100, destroy: !1, breakpoints: !1, classes: { root: q, slider: q + "__slider", track: q + "__track", list: q + "__list", slide: q + "__slide", container: q + "__slide__container", arrows: q + "__arrows", arrow: q + "__arrow", prev: q + "__arrow--prev", next: q + "__arrow--next", pagination: q + "__pagination", page: q + "__pagination__page", clone: q + "__slide--clone", progress: q + "__progress", bar: q + "__progress__bar", autoplay: q + "__autoplay", play: q + "__play", pause: q + "__pause", spinner: q + "__spinner", sr: q + "__sr" }, i18n: { prev: "Previous slide", next: "Next slide", first: "Go to first slide", last: "Go to last slide", slideX: "Go to slide %s", pageX: "Go to page %s", play: "Start autoplay", pause: "Pause autoplay" } }, R = 1, B = 2, F = 3, G = 4, X = 5; function V(t, n) { for (var e = 0; e < n.length; e++) { var i = n[e]; i.enumerable = i.enumerable || !1, i.configurable = !0, "value" in i && (i.writable = !0), Object.defineProperty(t, i.key, i) } } var U = function () { function t(t, e, i) { var o; void 0 === e && (e = {}), void 0 === i && (i = {}), this.root = t instanceof Element ? t : document.querySelector(t), j(this.root, "An invalid element/selector was given."), this.Components = null, this.Event = function () { var t = []; function n(t) { t.elm && t.elm.removeEventListener(t.event, t.handler, t.options) } return { on: function (n, e, i, o) { void 0 === i && (i = null), void 0 === o && (o = {}), n.split(" ").forEach((function (n) { i && i.addEventListener(n, e, o), t.push({ event: n, handler: e, elm: i, options: o }) })) }, off: function (e, i) { void 0 === i && (i = null), e.split(" ").forEach((function (e) { t = t.filter((function (t) { return !t || t.event !== e || t.elm !== i || (n(t), !1) })) })) }, emit: function (n) { for (var e = arguments.length, i = new Array(e > 1 ? e - 1 : 0), o = 1; o < e; o++)i[o - 1] = arguments[o]; t.forEach((function (t) { t.elm || t.event.split(".")[0] !== n || t.handler.apply(t, i) })) }, destroy: function () { t.forEach(n), t = [] } } }(), this.State = (o = R, { set: function (t) { o = t }, is: function (t) { return t === o } }), this.STATES = n, this._o = a(N, e), this._i = 0, this._c = i, this._e = {}, this._t = null } var e, i, s, u = t.prototype; return u.mount = function (t, n) { var e = this; void 0 === t && (t = this._e), void 0 === n && (n = this._t), this.State.set(R), this._e = t, this._t = n, this.Components = function (t, n, e) { var i = {}; return o(n, (function (n, e) { i[e] = n(t, i, e.toLowerCase()) })), e || (e = t.is(O) ? W : L), i.Transition = e(t, i), i }(this, a(this._c, t), n); try { o(this.Components, (function (t, n) { var i = t.required; void 0 === i || i ? t.mount && t.mount() : delete e.Components[n] })) } catch (t) { return void H(t.message) } var i = this.State; return i.set(B), o(this.Components, (function (t) { t.mounted && t.mounted() })), this.emit("mounted"), i.set(F), this.emit("ready"), E(this.root, { visibility: "visible" }), this.on("move drag", (function () { return i.set(G) })).on("moved dragged", (function () { return i.set(F) })), this }, u.sync = function (t) { return this.sibling = t, this }, u.on = function (t, n, e, i) { return void 0 === e && (e = null), void 0 === i && (i = {}), this.Event.on(t, n, e, i), this }, u.off = function (t, n) { return void 0 === n && (n = null), this.Event.off(t, n), this }, u.emit = function (t) { for (var n, e = arguments.length, i = new Array(e > 1 ? e - 1 : 0), o = 1; o < e; o++)i[o - 1] = arguments[o]; return (n = this.Event).emit.apply(n, [t].concat(i)), this }, u.go = function (t, n) { return void 0 === n && (n = this.options.waitForTransition), (this.State.is(F) || this.State.is(G) && !n) && this.Components.Controller.go(t, !1), this }, u.is = function (t) { return t === this._o.type }, u.add = function (t, n) { return void 0 === n && (n = -1), this.Components.Elements.add(t, n, this.refresh.bind(this)), this }, u.remove = function (t) { return this.Components.Elements.remove(t), this.refresh(), this }, u.refresh = function () { return this.emit("refresh:before").emit("refresh").emit("resize"), this }, u.destroy = function (t) { var n = this; if (void 0 === t && (t = !0), !this.State.is(R)) return r(this.Components).reverse().forEach((function (n) { n.destroy && n.destroy(t) })), this.emit("destroy", t), this.Event.destroy(), this.State.set(X), this; this.on("ready", (function () { return n.destroy(t) })) }, e = t, (i = [{ key: "index", get: function () { return this._i }, set: function (t) { this._i = parseInt(t) } }, { key: "length", get: function () { return this.Components.Elements.length } }, { key: "options", get: function () { return this._o }, set: function (t) { var n = this.State.is(R); n || this.emit("update"), this._o = a(this._o, t), n || this.emit("updated", this._o) } }, { key: "classes", get: function () { return this._o.classes } }, { key: "i18n", get: function () { return this._o.i18n } }]) && V(e.prototype, i), s && V(e, s), t }(), Y = function (t) { var n = z(t.root, "data-splide"); if (n) try { t.options = JSON.parse(n) } catch (t) { H(t.message) } return { mount: function () { t.State.is(R) && (t.index = t.options.start) } } }, J = "rtl", K = "ttb", Q = "update.slide", Z = function (t, n) { var e = t.root, i = t.classes, s = []; if (!e.id) { window.splide = window.splide || {}; var a = window.splide.uid || 0; window.splide.uid = ++a, e.id = "splide" + l(a) } var u = { mount: function () { var n = this; this.init(), t.on("refresh", (function () { n.destroy(), n.init() })).on("updated", (function () { S(e, c()), k(e, c()) })) }, destroy: function () { s.forEach((function (t) { t.destroy() })), s = [], S(e, c()) }, init: function () { var t = this; !function () { u.slider = g(e, i.slider), u.track = p(e, "." + i.track), u.list = g(u.track, i.list), j(u.track && u.list, "Track or list was not found."), u.slides = v(u.list, i.slide); var t = d(i.arrows); u.arrows = { prev: p(t, "." + i.prev), next: p(t, "." + i.next) }; var n = d(i.autoplay); u.bar = p(d(i.progress), "." + i.bar), u.play = p(n, "." + i.play), u.pause = p(n, "." + i.pause), u.track.id = u.track.id || e.id + "-track", u.list.id = u.list.id || e.id + "-list" }(), k(e, c()), this.slides.forEach((function (n, e) { t.register(n, e, -1) })) }, register: function (n, e, i) { var o = function (t, n, e, i) { var o = t.options.updateOnMove, s = "ready.slide updated.slide resized.slide moved.slide" + (o ? " move.slide" : ""), a = { slide: i, index: n, realIndex: e, container: g(i, t.classes.container), isClone: e > -1, mount: function () { var r = this; this.isClone || (i.id = t.root.id + "-slide" + l(n + 1)), t.on(s, (function () { return r.update() })).on(Q, c).on("click", (function () { return t.emit("click", r) }), i), o && t.on("move.slide", (function (t) { t === e && u(!0, !1) })), E(i, { display: "" }), this.styles = z(i, "style") || "" }, destroy: function () { t.off(s).off(Q).off("click", i), S(i, r(D)), c(), I(this.container, "style") }, update: function () { u(this.isActive(), !1), u(this.isVisible(), !0) }, isActive: function () { return t.index === n }, isVisible: function () { var n = this.isActive(); if (t.is(O) || n) return n; var e = Math.ceil, o = M(t.Components.Elements.track), r = M(i); return t.options.direction === K ? o.top <= r.top && r.bottom <= e(o.bottom) : o.left <= r.left && r.right <= e(o.right) }, isWithin: function (e, i) { var o = Math.abs(e - n); return t.is(T) || this.isClone || (o = Math.min(o, t.length - o)), o < i } }; function u(n, e) { var o = e ? "visible" : "active", r = D[o]; n ? (k(i, r), t.emit("" + o, a)) : P(i, r) && (S(i, r), t.emit(e ? "hidden" : "inactive", a)) } function c() { C(i, "style", a.styles) } return a }(t, e, i, n); o.mount(), s.push(o) }, getSlide: function (t) { return s.filter((function (n) { return n.index === t }))[0] }, getSlides: function (t) { return t ? s : s.filter((function (t) { return !t.isClone })) }, getSlidesByPage: function (e) { var i = n.Controller.toIndex(e), o = t.options, r = !1 !== o.focus ? 1 : o.perPage; return s.filter((function (t) { var n = t.index; return i <= n && n < i + r })) }, add: function (t, n, e) { if ("string" == typeof t && (t = y(t)), t instanceof Element) { var i = this.slides[n]; E(t, { display: "none" }), i ? (x(t, i), this.slides.splice(n, 0, t)) : (w(this.list, t), this.slides.push(t)), function (t, n) { var e = t.querySelectorAll("img"), i = e.length; if (i) { var r = 0; o(e, (function (t) { t.onload = t.onerror = function () { ++r === i && n() } })) } else n() }(t, (function () { e && e(t) })) } }, remove: function (t) { b(this.slides.splice(t, 1)[0]) }, each: function (t) { s.forEach(t) }, get length() { return this.slides.length }, get total() { return s.length } }; function c() { var n = i.root, e = t.options; return [n + "--" + e.type, n + "--" + e.direction, e.drag ? n + "--draggable" : "", e.isNavigation ? n + "--nav" : "", D.active] } function d(t) { return g(e, t) || g(u.slider, t) } return u }, $ = Math.floor, tt = function (t, n) { var e, i, o = { mount: function () { e = t.options, i = t.is(A), t.on("move", (function (n) { t.index = n })).on("updated refresh", (function (n) { e = n || e, t.index = c(t.index, 0, o.edgeIndex) })) }, go: function (t, e) { var i = this.trim(this.parse(t)); n.Track.go(i, this.rewind(i), e) }, parse: function (n) { var i = t.index, r = String(n).match(/([+\-<>]+)(\d+)?/), s = r ? r[1] : "", a = r ? parseInt(r[2]) : 0; switch (s) { case "+": i += a || 1; break; case "-": i -= a || 1; break; case ">": case "<": i = function (t, n, i) { if (t > -1) return o.toIndex(t); var r = e.perMove, s = i ? -1 : 1; if (r) return n + r * s; return o.toIndex(o.toPage(n) + s) }(a, i, "<" === s); break; default: i = parseInt(n) }return i }, toIndex: function (n) { if (r()) return n; var i = t.length, o = e.perPage, s = n * o; return i - o <= (s -= (this.pageLength * o - i) * $(s / i)) && s < i && (s = i - o), s }, toPage: function (n) { if (r()) return n; var i = t.length, o = e.perPage; return $(i - o <= n && n < i ? (i - 1) / o : n / o) }, trim: function (t) { return i || (t = e.rewind ? this.rewind(t) : c(t, 0, this.edgeIndex)), t }, rewind: function (t) { var n = this.edgeIndex; if (i) { for (; t > n;)t -= n + 1; for (; t < 0;)t += n + 1 } else t > n ? t = 0 : t < 0 && (t = n); return t }, isRtl: function () { return e.direction === J }, get pageLength() { var n = t.length; return r() ? n : Math.ceil(n / e.perPage) }, get edgeIndex() { var n = t.length; return n ? r() || e.isNavigation || i ? n - 1 : n - e.perPage : 0 }, get prevIndex() { var n = t.index - 1; return (i || e.rewind) && (n = this.rewind(n)), n > -1 ? n : -1 }, get nextIndex() { var n = t.index + 1; return (i || e.rewind) && (n = this.rewind(n)), t.index < n && n <= this.edgeIndex || 0 === n ? n : -1 } }; function r() { return !1 !== e.focus } return o }, nt = Math.abs, et = function (t, n) { var e, i, o, r = t.options.direction === K, s = t.is(O), a = t.options.direction === J, u = !1, d = a ? 1 : -1, f = { sign: d, mount: function () { i = n.Elements, e = n.Layout, o = i.list }, mounted: function () { var n = this; s || (this.jump(0), t.on("mounted resize updated", (function () { n.jump(t.index) }))) }, go: function (e, i, o) { var r = h(e), a = t.index; t.State.is(G) && u || (u = e !== i, o || t.emit("move", i, a, e), Math.abs(r - this.position) >= 1 || s ? n.Transition.start(e, i, a, this.toCoord(r), (function () { l(e, i, a, o) })) : e !== a && "move" === t.options.trimSpace ? n.Controller.go(e + e - a, o) : l(e, i, a, o)) }, jump: function (t) { this.translate(h(t)) }, translate: function (t) { E(o, { transform: "translate" + (r ? "Y" : "X") + "(" + t + "px)" }) }, cancel: function () { t.is(A) ? this.shift() : this.translate(this.position), E(o, { transition: "" }) }, shift: function () { var n = nt(this.position), e = nt(this.toPosition(0)), i = nt(this.toPosition(t.length)), o = i - e; n < e ? n += o : n > i && (n -= o), this.translate(d * n) }, trim: function (n) { return !t.options.trimSpace || t.is(A) ? n : c(n, d * (e.totalSize() - e.size - e.gap), 0) }, toIndex: function (t) { var n = this, e = 0, o = 1 / 0; return i.getSlides(!0).forEach((function (i) { var r = i.index, s = nt(n.toPosition(r) - t); s < o && (o = s, e = r) })), e }, toCoord: function (t) { return { x: r ? 0 : t, y: r ? t : 0 } }, toPosition: function (t) { var n = e.totalSize(t) - e.slideSize(t) - e.gap; return d * (n + this.offset(t)) }, offset: function (n) { var i = t.options.focus, o = e.slideSize(n); return "center" === i ? -(e.size - o) / 2 : -(parseInt(i) || 0) * (o + e.gap) }, get position() { var t = r ? "top" : a ? "right" : "left"; return M(o)[t] - (M(i.track)[t] - e.padding[t] * d) } }; function l(n, e, i, r) { E(o, { transition: "" }), u = !1, s || f.jump(e), r || t.emit("moved", e, i, n) } function h(t) { return f.trim(f.toPosition(t)) } return f }, it = function (t, n) { var e = [], i = 0, o = n.Elements, r = { mount: function () { var n = this; t.is(A) && (s(), t.on("refresh:before", (function () { n.destroy() })).on("refresh", s).on("resize", (function () { i !== a() && (n.destroy(), t.refresh()) }))) }, destroy: function () { b(e), e = [] }, get clones() { return e }, get length() { return e.length } }; function s() { r.destroy(), function (t) { var n = o.length, i = o.register; if (n) { for (var r = o.slides; r.length < t;)r = r.concat(r); r.slice(0, t).forEach((function (t, r) { var s = u(t); w(o.list, s), e.push(s), i(s, r + n, r % n) })), r.slice(-t).forEach((function (o, s) { var a = u(o); x(a, r[0]), e.push(a), i(a, s - t, (n + s - t % n) % n) })) } }(i = a()) } function a() { var n = t.options; if (n.clones) return n.clones; var e = n.autoWidth || n.autoHeight ? o.length : n.perPage, i = n.direction === K ? "Height" : "Width", r = h(t.root, n["fixed" + i]); return r && (e = Math.ceil(o.track["client" + i] / r)), e * (n.drag ? n.flickMaxPages + 1 : 1) } function u(n) { var e = n.cloneNode(!0); return k(e, t.classes.clone), I(e, "id"), e } return r }; function ot(t, n) { var e; return function () { e || (e = setTimeout((function () { t(), e = null }), n)) } } var rt = function (t, n) { var e, o, r = n.Elements, s = t.options.direction === K, a = (e = { mount: function () { t.on("resize load", ot((function () { t.emit("resize") }), t.options.throttle), window).on("resize", c).on("updated refresh", u), u(), this.totalSize = s ? this.totalHeight : this.totalWidth, this.slideSize = s ? this.slideHeight : this.slideWidth }, destroy: function () { I([r.list, r.track], "style") }, get size() { return s ? this.height : this.width } }, o = s ? function (t, n) { var e, i, o = n.Elements, r = t.root; return { margin: "marginBottom", init: function () { this.resize() }, resize: function () { i = t.options, e = o.track, this.gap = h(r, i.gap); var n = i.padding, s = h(r, n.top || n), a = h(r, n.bottom || n); this.padding = { top: s, bottom: a }, E(e, { paddingTop: f(s), paddingBottom: f(a) }) }, totalHeight: function (n) { void 0 === n && (n = t.length - 1); var e = o.getSlide(n); return e ? M(e.slide).bottom - M(o.list).top + this.gap : 0 }, slideWidth: function () { return h(r, i.fixedWidth || this.width) }, slideHeight: function (t) { if (i.autoHeight) { var n = o.getSlide(t); return n ? n.slide.offsetHeight : 0 } var e = i.fixedHeight || (this.height + this.gap) / i.perPage - this.gap; return h(r, e) }, get width() { return e.clientWidth }, get height() { var t = i.height || this.width * i.heightRatio; return j(t, '"height" or "heightRatio" is missing.'), h(r, t) - this.padding.top - this.padding.bottom } } }(t, n) : function (t, n) { var e, i = n.Elements, o = t.root, r = t.options; return { margin: "margin" + (r.direction === J ? "Left" : "Right"), height: 0, init: function () { this.resize() }, resize: function () { r = t.options, e = i.track, this.gap = h(o, r.gap); var n = r.padding, s = h(o, n.left || n), a = h(o, n.right || n); this.padding = { left: s, right: a }, E(e, { paddingLeft: f(s), paddingRight: f(a) }) }, totalWidth: function (n) { void 0 === n && (n = t.length - 1); var e = i.getSlide(n), o = 0; if (e) { var s = M(e.slide), a = M(i.list); o = r.direction === J ? a.right - s.left : s.right - a.left, o += this.gap } return o }, slideWidth: function (t) { if (r.autoWidth) { var n = i.getSlide(t); return n ? n.slide.offsetWidth : 0 } var e = r.fixedWidth || (this.width + this.gap) / r.perPage - this.gap; return h(o, e) }, slideHeight: function () { var t = r.height || r.fixedHeight || this.width * r.heightRatio; return h(o, t) }, get width() { return e.clientWidth - this.padding.left - this.padding.right } } }(t, n), i(o).forEach((function (t) { e[t] || Object.defineProperty(e, t, Object.getOwnPropertyDescriptor(o, t)) })), e); function u() { a.init(), E(t.root, { maxWidth: f(t.options.width) }), r.each((function (t) { t.slide.style[a.margin] = f(a.gap) })), c() } function c() { var n = t.options; a.resize(), E(r.track, { height: f(a.height) }); var e = n.autoHeight ? null : f(a.slideHeight()); r.each((function (t) { E(t.container, { height: e }), E(t.slide, { width: n.autoWidth ? null : f(a.slideWidth(t.index)), height: t.container ? null : e }) })), t.emit("resized") } return a }, st = Math.abs, at = function (t, n) { var e, i, r, s, a = n.Track, u = n.Controller, d = t.options.direction === K, f = d ? "y" : "x", l = { disabled: !1, mount: function () { var e = this, i = n.Elements, r = i.track; t.on("touchstart mousedown", h, r).on("touchmove mousemove", g, r, { passive: !1 }).on("touchend touchcancel mouseleave mouseup dragend", v, r).on("mounted refresh", (function () { o(i.list.querySelectorAll("img, a"), (function (n) { t.off("dragstart", n).on("dragstart", (function (t) { t.preventDefault() }), n, { passive: !1 }) })) })).on("mounted updated", (function () { e.disabled = !t.options.drag })) } }; function h(t) { l.disabled || s || p(t) } function p(t) { e = a.toCoord(a.position), i = m(t, {}), r = i } function g(n) { if (i) if (r = m(n, i), s) { if (n.cancelable && n.preventDefault(), !t.is(O)) { var o = e[f] + r.offset[f]; a.translate(function (n) { if (t.is(T)) { var e = a.sign, i = e * a.trim(a.toPosition(0)), o = e * a.trim(a.toPosition(u.edgeIndex)); (n *= e) < i ? n = i - 7 * Math.log(i - n) : n > o && (n = o + 7 * Math.log(n - o)), n *= e } return n }(o)) } } else (function (n) { var e = n.offset; if (t.State.is(G) && t.options.waitForTransition) return !1; var i = 180 * Math.atan(st(e.y) / st(e.x)) / Math.PI; d && (i = 90 - i); return i < t.options.dragAngleThreshold })(r) && (t.emit("drag", i), s = !0, a.cancel(), p(n)) } function v() { i = null, s && (t.emit("dragged", r), function (e) { var i = e.velocity[f], o = st(i); if (o > 0) { var r = t.options, s = t.index, d = i < 0 ? -1 : 1, l = s; if (!t.is(O)) { var h = a.position; o > r.flickVelocityThreshold && st(e.offset[f]) < r.swipeDistanceThreshold && (h += d * Math.min(o * r.flickPower, n.Layout.size * (r.flickMaxPages || 1))), l = a.toIndex(h) } l === s && o > .1 && (l = s + d * a.sign), t.is(T) && (l = c(l, 0, u.edgeIndex)), u.go(l, r.isNavigation) } }(r), s = !1) } function m(t, n) { var e = t.timeStamp, i = t.touches, o = i ? i[0] : t, r = o.clientX, s = o.clientY, a = n.to || {}, u = a.x, c = void 0 === u ? r : u, d = a.y, f = { x: r - c, y: s - (void 0 === d ? s : d) }, l = e - (n.time || 0); return { to: { x: r, y: s }, offset: f, time: e, velocity: { x: f.x / l, y: f.y / l } } } return l }, ut = function (t, n) { var e = !1; function i(t) { e && (t.preventDefault(), t.stopPropagation(), t.stopImmediatePropagation()) } return { required: t.options.drag, mount: function () { t.on("click", i, n.Elements.track, { capture: !0 }).on("drag", (function () { e = !0 })).on("dragged", (function () { setTimeout((function () { e = !1 })) })) } } }, ct = 1, dt = 2, ft = 3, lt = function (t, n, e) { var i, o, r, s = t.classes, a = t.root, u = n.Elements; function c() { var r = n.Controller, s = r.prevIndex, a = r.nextIndex, u = t.length > t.options.perPage || t.is(A); i.disabled = s < 0 || !u, o.disabled = a < 0 || !u, t.emit(e + ":updated", i, o, s, a) } function d(n) { return y('<button class="' + s.arrow + " " + (n ? s.prev : s.next) + '" type="button"><svg xmlns="http://www.w3.org/2000/svg"\tviewBox="0 0 40 40"\twidth="40"\theight="40"><path d="' + (t.options.arrowPath || "m15.5 0.932-4.3 4.38 14.5 14.6-14.5 14.5 4.3 4.4 14.6-14.6 4.4-4.3-4.4-4.4-14.6-14.6z") + '" />') } return { required: t.options.arrows, mount: function () { i = u.arrows.prev, o = u.arrows.next, i && o || !t.options.arrows || (i = d(!0), o = d(!1), r = !0, function () { var n = m("div", { class: s.arrows }); w(n, i), w(n, o); var e = u.slider, r = "slider" === t.options.arrows && e ? e : a; x(n, r.firstElementChild) }()), i && o && t.on("click", (function () { t.go("<") }), i).on("click", (function () { t.go(">") }), o).on("mounted move updated refresh", c), this.arrows = { prev: i, next: o } }, mounted: function () { t.emit(e + ":mounted", i, o) }, destroy: function () { I([i, o], "disabled"), r && b(i.parentElement) } } }, ht = "move.page", pt = "updated.page refresh.page", gt = function (t, n, e) { var i = {}, o = n.Elements, r = { mount: function () { var n = t.options.pagination; if (n) { i = function () { var n = t.options, e = t.classes, i = m("ul", { class: e.pagination }), r = o.getSlides(!1).filter((function (t) { return !1 !== n.focus || t.index % n.perPage == 0 })).map((function (n, r) { var s = m("li", {}), a = m("button", { class: e.page, type: "button" }); return w(s, a), w(i, s), t.on("click", (function () { t.go(">" + r) }), a), { li: s, button: a, page: r, Slides: o.getSlidesByPage(r) } })); return { list: i, items: r } }(); var e = o.slider; w("slider" === n && e ? e : t.root, i.list), t.on(ht, s) } t.off(pt).on(pt, (function () { r.destroy(), t.options.pagination && (r.mount(), r.mounted()) })) }, mounted: function () { if (t.options.pagination) { var n = t.index; t.emit(e + ":mounted", i, this.getItem(n)), s(n, -1) } }, destroy: function () { b(i.list), i.items && i.items.forEach((function (n) { t.off("click", n.button) })), t.off(ht), i = {} }, getItem: function (t) { return i.items[n.Controller.toPage(t)] }, get data() { return i } }; function s(n, o) { var s = r.getItem(o), a = r.getItem(n), u = D.active; s && S(s.button, u), a && k(a.button, u), t.emit(e + ":updated", i, s, a) } return r }, vt = "data-splide-lazy", mt = "data-splide-lazy-srcset", yt = "aria-current", bt = "aria-controls", wt = "aria-label", xt = "aria-hidden", Et = "tabindex", _t = { ltr: { ArrowLeft: "<", ArrowRight: ">", Left: "<", Right: ">" }, rtl: { ArrowLeft: ">", ArrowRight: "<", Left: ">", Right: "<" }, ttb: { ArrowUp: "<", ArrowDown: ">", Up: "<", Down: ">" } }, kt = function (t, n) { var e = t.i18n, i = n.Elements, o = [xt, Et, bt, wt, yt, "role"]; function r(n, e) { C(n, xt, !e), t.options.slideFocus && C(n, Et, e ? 0 : -1) } function s(t, n) { var e = i.track.id; C(t, bt, e), C(n, bt, e) } function a(n, i, o, r) { var s = t.index, a = o > -1 && s < o ? e.last : e.prev, u = r > -1 && s > r ? e.first : e.next; C(n, wt, a), C(i, wt, u) } function u(n, i) { i && C(i.button, yt, !0), n.items.forEach((function (n) { var i = t.options, o = d(!1 === i.focus && i.perPage > 1 ? e.pageX : e.slideX, n.page + 1), r = n.button, s = n.Slides.map((function (t) { return t.slide.id })); C(r, bt, s.join(" ")), C(r, wt, o) })) } function c(t, n, e) { n && I(n.button, yt), e && C(e.button, yt, !0) } function f(t) { i.each((function (n) { var i = n.slide, o = n.realIndex; h(i) || C(i, "role", "button"); var r = o > -1 ? o : n.index, s = d(e.slideX, r + 1), a = t.Components.Elements.getSlide(r); C(i, wt, s), a && C(i, bt, a.slide.id) })) } function l(t, n) { var e = t.slide; n ? C(e, yt, !0) : I(e, yt) } function h(t) { return "BUTTON" === t.tagName } return { required: t.options.accessibility, mount: function () { t.on("visible", (function (t) { r(t.slide, !0) })).on("hidden", (function (t) { r(t.slide, !1) })).on("arrows:mounted", s).on("arrows:updated", a).on("pagination:mounted", u).on("pagination:updated", c).on("refresh", (function () { I(n.Clones.clones, o) })), t.options.isNavigation && t.on("navigation:mounted navigation:updated", f).on("active", (function (t) { l(t, !0) })).on("inactive", (function (t) { l(t, !1) })), ["play", "pause"].forEach((function (t) { var n = i[t]; n && (h(n) || C(n, "role", "button"), C(n, bt, i.track.id), C(n, wt, e[t])) })) }, destroy: function () { var t = n.Arrows, e = t ? t.arrows : {}; I(i.slides.concat([e.prev, e.next, i.play, i.pause]), o) } } }, St = "move.sync", Pt = "mouseup touchend", Ct = [" ", "Enter", "Spacebar"], zt = { Options: Y, Breakpoints: function (t) { var n, e, i = t.options.breakpoints, o = ot(s, 50), r = []; function s() { var o, s = (o = r.filter((function (t) { return t.mql.matches }))[0]) ? o.point : -1; if (s !== e) { e = s; var a = t.State, u = i[s] || n, c = u.destroy; c ? (t.options = n, t.destroy("completely" === c)) : (a.is(X) && t.mount(), t.options = u) } } return { required: i && matchMedia, mount: function () { r = Object.keys(i).sort((function (t, n) { return +t - +n })).map((function (t) { return { point: t, mql: matchMedia("(max-width:" + t + "px)") } })), this.destroy(!0), addEventListener("resize", o), n = t.options, s() }, destroy: function (t) { t && removeEventListener("resize", o) } } }, Controller: tt, Elements: Z, Track: et, Clones: it, Layout: rt, Drag: at, Click: ut, Autoplay: function (t, n, e) { var i, o = [], r = n.Elements, s = { required: t.options.autoplay, mount: function () { var n = t.options; r.slides.length > n.perPage && (i = function (t, n, e) { var i, o, r, s = window.requestAnimationFrame, a = !0, u = function u(c) { a || (i || (i = c, r && r < 1 && (i -= r * n)), r = (o = c - i) / n, o >= n && (i = 0, r = 1, t()), e && e(r), s(u)) }; return { pause: function () { a = !0, i = 0 }, play: function (t) { i = 0, t && (r = 0), a && (a = !1, s(u)) } } }((function () { t.go(">") }), n.interval, (function (n) { t.emit(e + ":playing", n), r.bar && E(r.bar, { width: 100 * n + "%" }) })), function () { var n = t.options, e = t.sibling, i = [t.root, e ? e.root : null]; n.pauseOnHover && (a(i, "mouseleave", ct, !0), a(i, "mouseenter", ct, !1)); n.pauseOnFocus && (a(i, "focusout", dt, !0), a(i, "focusin", dt, !1)); r.play && t.on("click", (function () { s.play(dt), s.play(ft) }), r.play); r.pause && a([r.pause], "click", ft, !1); t.on("move refresh", (function () { s.play() })).on("destroy", (function () { s.pause() })) }(), this.play()) }, play: function (n) { void 0 === n && (n = 0), (o = o.filter((function (t) { return t !== n }))).length || (t.emit(e + ":play"), i.play(t.options.resetProgress)) }, pause: function (n) { void 0 === n && (n = 0), i.pause(), -1 === o.indexOf(n) && o.push(n), 1 === o.length && t.emit(e + ":pause") } }; function a(n, e, i, o) { n.forEach((function (n) { t.on(e, (function () { s[o ? "play" : "pause"](i) }), n) })) } return s }, Cover: function (t, n) { function e(t) { n.Elements.each((function (n) { var e = g(n.slide, "IMG") || g(n.container, "IMG"); e && e.src && i(e, t) })) } function i(t, n) { E(t.parentElement, { background: n ? "" : 'center/cover no-repeat url("' + t.src + '")' }), E(t, { display: n ? "" : "none" }) } return { required: t.options.cover, mount: function () { t.on("lazyload:loaded", (function (t) { i(t, !1) })), t.on("mounted updated refresh", (function () { return e(!1) })) }, destroy: function () { e(!0) } } }, Arrows: lt, Pagination: gt, LazyLoad: function (t, n, e) { var i, r, s = t.options, a = "sequential" === s.lazyLoad; function u() { r = [], i = 0 } function c(n) { n = isNaN(n) ? t.index : n, (r = r.filter((function (t) { return !t.Slide.isWithin(n, s.perPage * (s.preloadPages + 1)) || (d(t.img, t.Slide), !1) })))[0] || t.off("moved." + e) } function d(n, e) { k(e.slide, D.loading); var i = m("span", { class: t.classes.spinner }); w(n.parentElement, i), n.onload = function () { l(n, i, e, !1) }, n.onerror = function () { l(n, i, e, !0) }, C(n, "srcset", z(n, mt) || ""), C(n, "src", z(n, vt) || "") } function f() { if (i < r.length) { var t = r[i]; d(t.img, t.Slide) } i++ } function l(n, i, o, r) { S(o.slide, D.loading), r || (b(i), E(n, { display: "" }), t.emit(e + ":loaded", n).emit("resize")), a && f() } return { required: s.lazyLoad, mount: function () { t.on("mounted refresh", (function () { u(), n.Elements.each((function (t) { o(t.slide.querySelectorAll("[data-splide-lazy], [" + mt + "]"), (function (n) { n.src || n.srcset || (r.push({ img: n, Slide: t }), E(n, { display: "none" })) })) })), a && f() })), a || t.on("mounted refresh moved." + e, c) }, destroy: u } }, Keyboard: function (t) { var n; return { mount: function () { t.on("mounted updated", (function () { var e = t.options, i = t.root, o = _t[e.direction], r = e.keyboard; n && (t.off("keydown", n), I(i, Et)), r && ("focused" === r ? (n = i, C(i, Et, 0)) : n = document, t.on("keydown", (function (n) { o[n.key] && t.go(o[n.key]) }), n)) })) } } }, Sync: function (t) { var n = t.sibling, e = n && n.options.isNavigation; function i() { t.on(St, (function (t, e, i) { n.off(St).go(n.is(A) ? i : t, !1), o() })) } function o() { n.on(St, (function (n, e, o) { t.off(St).go(t.is(A) ? o : n, !1), i() })) } function r() { n.Components.Elements.each((function (n) { var e = n.slide, i = n.index; t.off(Pt, e).on(Pt, (function (t) { t.button && 0 !== t.button || s(i) }), e), t.off("keyup", e).on("keyup", (function (t) { Ct.indexOf(t.key) > -1 && (t.preventDefault(), s(i)) }), e, { passive: !1 }) })) } function s(e) { t.State.is(F) && n.go(e) } return { required: !!n, mount: function () { i(), o(), e && (r(), t.on("refresh", (function () { setTimeout((function () { r(), n.emit("navigation:updated", t) })) }))) }, mounted: function () { e && n.emit("navigation:mounted", t) } } }, A11y: kt }; var It = function (t) { var n, e; function i(n, e) { return t.call(this, n, e, zt) || this } return e = t, (n = i).prototype = Object.create(e.prototype), n.prototype.constructor = n, n.__proto__ = e, i }(U); window.Splide = It }();

var JohnLocke = (function () {

    function JohnLocke() {

    }

    JohnLocke.on = function () {

        var scrollBarWidth = JohnLocke.getScrollBarWidth();

        document.documentElement.classList.add(JohnLocke.lockClass);

        if (!document.documentElement.classList.contains('html--one-page-scroll') || document.documentElement.classList.contains('html--one-page-scroll-disabled')) {

            JohnLocke.headerElement.style.paddingRight = scrollBarWidth + 'px';

            JohnLocke.mainElement.style.paddingRight = scrollBarWidth + 'px';

        }

        if (!document.documentElement.classList.contains('html--one-page-scroll-disabled') && typeof disableOnePageScroll === 'function') {

            disableOnePageScroll();

        }

    };

    JohnLocke.off = function () {

        JohnLocke.headerElement.style.paddingRight = '0px';

        JohnLocke.mainElement.style.paddingRight = '0px';

        document.documentElement.classList.remove(JohnLocke.lockClass);

        if (!document.documentElement.classList.contains('html--one-page-scroll-disabled') && typeof enableOnePageScroll === 'function') {

            enableOnePageScroll();

        }

    };

    JohnLocke.getScrollBarWidth = function () {

        var outer = document.createElement('div');

        outer.style.visibility = 'hidden';

        outer.style.overflow = 'scroll';

        outer.style.msOverflowStyle = 'scrollbar';

        document.body.appendChild(outer);

        var inner = document.createElement('div');

        outer.appendChild(inner);

        var scrollbarWidth = (outer.offsetWidth - inner.offsetWidth);

        outer.parentNode.removeChild(outer);

        return scrollbarWidth;

    };

    ;

    JohnLocke.lockClass = 'html--john-locke';

    JohnLocke.headerElement = document.querySelector('[data-header]');

    JohnLocke.mainElement = document.querySelector('[data-main]');

    return JohnLocke;

}());

new JohnLocke();

window.JohnLocke = JohnLocke;


var Header = (function () {
    function Header() {
        this.toggleElement = document.querySelector('[data-header-toggle]');
        this.navElement = document.querySelector('[data-header-nav]');
        this.dimmerElement = document.querySelector('[data-header-dimmer]');
        this.allowToggle = true;
        if (!Header.headerElement || !this.toggleElement || !this.navElement)
            return;
        this.firstNavElement = this.navElement.querySelector('[data-header-nav-link="first"]');
        this.handleToggle();
        this.addNavPadding();
        this.handleScroll();
    }
    Header.prototype.handleToggle = function () {
        var _this = this;
        var self = this;
        self.toggleElement.addEventListener('click', function () {
            if (!self.allowToggle)
                return;
            self.allowToggle = false;
            var isExpanded = _this.navIsOpen();
            if (isExpanded) {
                self.navElement.classList.remove('is-open');
                JohnLocke.off();
                setTimeout(function () {
                    self.navElement.classList.remove('is-animated');
                    self.allowToggle = true;
                }, 300);
            }
            else {
                self.navElement.classList.add('is-animated');
                JohnLocke.on();
                if (self.firstNavElement)
                    self.firstNavElement.focus();
                setTimeout(function () {
                    self.navElement.classList.add('is-open');
                    setTimeout(function () { self.allowToggle = true; }, 300);
                }, 10);
            }
            var newExpandedValue = isExpanded ? 'false' : 'true';
            self.toggleElement.setAttribute('aria-expanded', newExpandedValue);
        });
        document.addEventListener('keyup', function (e) {
            var key = Helpers.getKeypressed(e);
            if (key === 'Escape' || key === 'Esc' || key === 27) {
                if (_this.navIsOpen())
                    self.toggleElement.click();
            }
        });
        self.dimmerElement.addEventListener('click', function () {
            if (_this.navIsOpen())
                self.toggleElement.click();
        });
    };
    Header.prototype.addNavPadding = function () {
        var self = this;
        var setPadding = Helpers.debounce(function () {
            var headerHeight = Header.headerElement.offsetHeight + 'px';
            self.navElement.style.paddingTop = headerHeight;
            self.navElement.style.paddingBottom = headerHeight;
        }, 200);
        setPadding();
        window.addEventListener('resize', setPadding);
    };
    Header.prototype.handleScroll = function () {
        var self = this;
        var setClass = Helpers.debounce(function () {
            var headerHeight = Header.headerElement.offsetHeight;
            var scrollTop = window.scrollY;
            if (scrollTop > headerHeight) {
                Header.applyBackground();
            }
            else {
                Header.killBackground();
            }
        }, 10);
        setClass();
        window.addEventListener('scroll', setClass);
    };
    Header.prototype.navIsOpen = function () {
        return this.toggleElement.getAttribute('aria-expanded') === 'true';
    };
    Header.applyBackground = function () {
        if (Header.headerElement) {
            Header.headerElement.classList.add(Header.backgroundClass);
        }
    };
    Header.killBackground = function () {
        if (Header.headerElement) {
            Header.headerElement.classList.remove(Header.backgroundClass);
        }
    };
    Header.headerElement = document.querySelector('[data-header]');
    Header.backgroundClass = 'c-header--background';
    return Header;
}());
new Header();


var BackgroundImage = (function () {

    function BackgroundImage() {

        this.backgroundImageElements = document.querySelectorAll('[data-background-image]');

        if (!this.backgroundImageElements.length)

            return;

        var self = this;

        var changeBackgrounds = Helpers.debounce(function () {

            Helpers.scopedLoop(self.backgroundImageElements, function (backgroundImageElement, i, context) {

                context.setup(backgroundImageElement, self);

            }, self);

        }, 100);

        changeBackgrounds();

        window.addEventListener('resize', changeBackgrounds);

    }

    BackgroundImage.prototype.setup = function (backgroundImageElement, self) {

        var imageUrl = backgroundImageElement.getAttribute('data-background-image-small');

        if (self.isLarge()) {

            imageUrl = backgroundImageElement.getAttribute('data-background-image-large');

        }

        else if (self.isMedium()) {

            imageUrl = backgroundImageElement.getAttribute('data-background-image-medium');

        }

        backgroundImageElement.style.backgroundImage = "url(" + imageUrl + ")";

        backgroundImageElement.setAttribute('data-background-image', 'loaded');

    };

    BackgroundImage.prototype.isMedium = function () {

        return window.matchMedia('(min-width: 500px)').matches;

    };

    BackgroundImage.prototype.isLarge = function () {

        return window.matchMedia('(min-width: 1100px)').matches;

    };

    return BackgroundImage;

}());

new BackgroundImage();


var OnePage = (function () {
    function OnePage() {
        this.onePageSelector = '[data-one-page]';
        this.onePageElement = document.querySelector(this.onePageSelector);
        this.onePageItemSelector = '[data-one-page-item]';
        this.onePageItemElements = document.querySelectorAll(this.onePageItemSelector);
        this.pastClass = 'c-one-page__item--past';
        if (!this.onePageElement || !this.onePageItemElements.length)
            return;
        this.setup();
    }
    OnePage.prototype.setup = function () {
        var self = this;
        var nativeScroll = self.onePageElement.hasAttribute('data-one-page-native-scroll');
        onePageScroll(self.onePageSelector, {
            sectionContainer: self.onePageItemSelector,
            easing: 'ease',
            animationTime: 700,
            updateURL: true,
            responsiveFallback: nativeScroll ? 980 : false,
            beforeMove: function (index, nextElement) {
                var zeroBasedIndex = index - 1;
                self.controlVideos(zeroBasedIndex);
                if (!nextElement)
                    return;
                var prevItems = Helpers.prevAll(nextElement, 99);
                var nextItems = Helpers.nextAll(nextElement, 99);
                Helpers.scopedLoop(prevItems, function (element, i, context) {
                    element.classList.add(context.pastClass);
                }, self);
                Helpers.scopedLoop(nextItems, function (element, i, context) {
                    element.classList.remove(context.pastClass);
                }, self);
                if (nextElement.classList.contains('c-one-page__item--split')) {
                    Header.applyBackground();
                }
                else {
                    Header.killBackground();
                }
            },
            afterMove: function (index) {
                self.loadImageryWithinSlide(index);
            }
        });
        self.controlVideos(0);
        self.addAccessibleText();
        self.setHeightOfItems();
        if (self.onePageItemElements.length) {
            self.loadImageryWithinSlide(1);
        }
    };
    OnePage.prototype.loadImageryWithinSlide = function (nextIndex) {
        var self = this;
        if (nextIndex >= self.onePageItemElements.length)
            return;
        var nextSlide = self.onePageItemElements[nextIndex];
        if (nextSlide.querySelector('[data-slideshow]'))
            return;
        var images = nextSlide.querySelectorAll('img[loading="lazy"]');
        if (!images.length)
            return;
        Helpers.scopedLoop(images, function (image, i, context) {
            image.setAttribute('loading', 'eager');
        }, self);
    };
    OnePage.prototype.controlVideos = function (currentIndex) {
        var self = this;
        Helpers.scopedLoop(self.onePageItemElements, function (element, i, context) {
            var videoEventName = element.hasAttribute('data-one-page-item-video-event') ? element.getAttribute('data-one-page-item-video-event') : '';
            if (videoEventName !== '') {
                if (currentIndex === i) {
                    var videoEvent = Helpers.createCustomEventForDispatch(videoEventName, { action: 'play' });
                    document.dispatchEvent(videoEvent);
                }
                else {
                    var videoEvent = Helpers.createCustomEventForDispatch(videoEventName, { action: 'pause' });
                    document.dispatchEvent(videoEvent);
                }
            }
        }, self);
    };
    OnePage.prototype.addAccessibleText = function () {
        var paginationLinks = document.querySelectorAll('.onepage-pagination a');
        Helpers.scopedLoop(paginationLinks, function (element, i, context) {
            var elementIndex = element.getAttribute('data-index');
            element.insertAdjacentHTML('beforeend', '<span class="u-visuallyhidden">Navigate to slide ' + elementIndex + '</span>');
        }, self);
    };
    OnePage.prototype.setHeightOfItems = function () {
        var setHeightElements = document.querySelectorAll('[data-one-page-item="setHeight"]');
        if (!setHeightElements)
            return;
        var lastWidth = 0;
        var setHeights = Helpers.debounce(function () {
            Helpers.scopedLoop(setHeightElements, function (element, i, context) {
                if (window.matchMedia('(min-height: 600px)').matches) {
                    if (window.innerWidth !== lastWidth) {
                        element.style.height = window.innerHeight + 'px';
                    }
                    lastWidth = window.innerWidth;
                }
                else {
                    element.removeAttribute('style');
                }
            }, self);
        }, 100);
        setHeights();
        window.addEventListener('resize', setHeights);
    };
    return OnePage;
}());
new OnePage();

var Slideshow = (function () {
    function Slideshow() {
        this.slideshowElements = document.querySelectorAll('[data-slideshow]');
        this.initClass = 'c-slideshow--initialised';
        this.slideshowInstances = {};
        this.slideshowClasses = {
            prev: 'c-slideshow__button c-slideshow__button--left',
            next: 'c-slideshow__button c-slideshow__button--right',
            slider: 'c-slideshow',
            track: 'c-slideshow__inner',
            list: 'c-slideshow__list',
            slide: 'c-slideshow__item'
        };
        if (!this.slideshowElements.length)
            return;
        Helpers.scopedLoop(this.slideshowElements, function (slideshowElement, i, context) {
            var name = 'carousel_' + i;
            context.setup(slideshowElement, name, context);
        }, this);
    }
    Slideshow.prototype.setup = function (slideshowElement, name, self) {
        var _this = this;
        var custom = slideshowElement.getAttribute('data-slideshow-custom') || false;
        if (custom) {
            this.initialiseCustomSlideshow(slideshowElement, name, custom);
            return;
        }
        this.slideshowInstances[name] = new Splide(slideshowElement, {
            pagination: false,
            drag: true,
            classes: this.slideshowClasses
        });
        this.slideshowInstances[name].on('mounted', function () {
            slideshowElement.classList.add(self.initClass);
            _this.loadImageryWithinSlide(1, _this.slideshowInstances[name].root);
        });
        this.slideshowInstances[name].on('moved', function () {
            var nextIndex = _this.slideshowInstances[name].index + 1;
            _this.loadImageryWithinSlide(nextIndex, _this.slideshowInstances[name].root);
        });
        this.slideshowInstances[name].mount();
    };
    Slideshow.prototype.loadImageryWithinSlide = function (nextIndex, element) {
        var self = this;
        var slides = element.querySelectorAll('.c-slideshow__item');
        if (nextIndex >= slides.length)
            return;
        var images = slides[nextIndex].querySelectorAll('img[loading="lazy"]');
        if (!images.length)
            return;
        Helpers.scopedLoop(images, function (image, i, context) {
            image.setAttribute('loading', 'eager');
        }, self);
    };
    Slideshow.prototype.initialiseCustomSlideshow = function (slideshowElement, name, custom) {
        var self = this;
        if (custom === 'stackSmall') {
            var mediaQuery_1 = '(min-height:600px)';
            self.initialiseStackSmall(slideshowElement, name, mediaQuery_1);
            window.matchMedia(mediaQuery_1).addListener(function () { self.initialiseStackSmall(slideshowElement, name, mediaQuery_1); });
        }
    };
    Slideshow.prototype.initialiseStackSmall = function (slideshowElement, name, mediaQuery) {
        var self = this;
        if (self.slideshowInstances[name]) {
            self.slideshowInstances[name].destroy(true);
            self.slideshowInstances[name] = null;
        }
        if (window.matchMedia(mediaQuery).matches) {
            self.slideshowInstances[name] = new Splide(slideshowElement, {
                pagination: false,
                drag: true,
                classes: self.slideshowClasses
            });
            self.slideshowInstances[name].on('mounted', function () {
                slideshowElement.classList.add(self.initClass);
                self.loadImageryWithinSlide(1, self.slideshowInstances[name].root);
            });
            self.slideshowInstances[name].on('moved', function () {
                var nextIndex = self.slideshowInstances[name].index + 1;
                self.loadImageryWithinSlide(nextIndex, self.slideshowInstances[name].root);
            });
            self.slideshowInstances[name].mount();
        }
    };
    return Slideshow;
}());
new Slideshow();

var Video = (function () {
    function Video() {
        this.videoElements = document.querySelectorAll('[data-video]');
        this.videoInstances = {};
        if (!this.videoElements.length)
            return;
        Helpers.scopedLoop(this.videoElements, function (videoElement, i, context) {
            context.setup(videoElement, context, i);
        }, this);
    }
    Video.prototype.setup = function (videoElement, context, videoIndex) {
        var videoSmallSource = videoElement.getAttribute('data-video-small-source');
        var videoLargeSource = videoElement.getAttribute('data-video-large-source');
        var videoClass = videoElement.hasAttribute('data-video-class') ? videoElement.getAttribute('data-video-class') : '';
        var videoMuted = videoElement.hasAttribute('data-video-muted') && videoElement.getAttribute('data-video-muted').toLowerCase() === 'true';
        var videoLoops = videoElement.hasAttribute('data-video-loops') && videoElement.getAttribute('data-video-loops').toLowerCase() === 'true';
        var videoEventListener = videoElement.hasAttribute('data-video-event-listener') ? videoElement.getAttribute('data-video-event-listener') : '';
        var videoSource = window.matchMedia('(min-width: 641px)').matches ? videoLargeSource : videoSmallSource;
        var videoMarkUp = "\n\t\t<video class=\"" + videoClass + "\" preload=\"metadata\" playsinline poster=\"data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7\">\n\t\t\t<source src=\"" + videoSource + "\" type=\"video/mp4\" />\n\t\t</video>\n\t\t";
        videoElement.insertAdjacentHTML('beforeend', videoMarkUp);
        context.videoInstances['video_' + videoIndex] = videoElement.querySelector('video');
        if (videoMuted) {
            context.videoInstances['video_' + videoIndex].muted = true;
        }
        if (videoLoops) {
            context.videoInstances['video_' + videoIndex].addEventListener('ended', function () {
                context.videoInstances['video_' + videoIndex].play();
            });
        }
        if (videoEventListener !== '') {
            document.addEventListener(videoEventListener, function (e) {
                var action = e.detail.action;
                if (action === 'play') {
                    context.videoInstances['video_' + videoIndex].play();
                }
                else if (action === 'pause') {
                    context.videoInstances['video_' + videoIndex].pause();
                }
            });
        }
    };
    return Video;
}());
new Video();

var Intersection = (function () {
    function Intersection() {
        this.attributeName = 'data-intersection-observer';
        this.intersectionElements = document.querySelectorAll('[' + this.attributeName + ']');
        if (!this.intersectionElements.length)
            return;
        this.setup();
    }
    Intersection.prototype.setup = function () {
        var self = this;
        if (self.isSupported()) {
            self.observer = new IntersectionObserver(function (entries) {
                Helpers.scopedLoop(entries, function (entry, i, context) {
                    if (entry.isIntersecting) {
                        entry.target.setAttribute(self.attributeName, 'visible');
                    }
                }, self);
            }, {
                threshold: 0.25
            });
            Helpers.scopedLoop(self.intersectionElements, function (element, i, context) {
                self.observer.observe(element);
            }, self);
            return;
        }
        Helpers.scopedLoop(self.intersectionElements, function (element, i, context) {
            element.setAttribute(self.attributeName, 'visible');
        }, self);
    };
    Intersection.prototype.isSupported = function () {
        return 'IntersectionObserver' in window && 'IntersectionObserverEntry' in window && 'intersectionRatio' in window.IntersectionObserverEntry.prototype;
    };
    return Intersection;
}());
new Intersection();

